/**
 * by 菲鸽 on 2024-03-06
 * 路由拦截，通常也是登录拦截
 * 可以设置路由白名单，或者黑名单，看业务需要选哪一个
 * 我这里应为大部分都可以随便进入，所以使用黑名单
 */
import type { PageInstance } from '@/utils'
import { appendCurrentChannelId, getCurrentChannelId, useChannelStore } from '@/store/channel'
import { readChannelId } from '@/utils/channel'
import { useUserStore } from '@/store'
import { needLoginPages as _needLoginPages, getNeedLoginPages, getLastPage } from '@/utils'
import { tabbarStore, isPageTabbar } from '@/tabbar/store'
// #ifdef MP-WEIXIN
import { isEnabled, getUrlParams, clear as clearFingerprint } from '@/utils/fingerprint'
// #endif

// TODO Check
const loginRoute = '/pages/login/index'

const isLogined = () => {
  const userStore = useUserStore()
  return userStore.isLogined
}

const isDev = import.meta.env.DEV
import { LDBridge } from '@/utils/handleBrdge'

// 页面路由参数中使用的 key
const PROMO_ID_KEY = 'promotionfingerprintId'
const TRACE_ID_KEY = 'promotionTraceId'

/**
 * 如目标 url 未携带推广码且指纹系统已启用，则自动追加 promotionfingerprintId 和 promotionTraceId
 * 
 * 核心逻辑：
 * - 使用 fingerprint 模块的 isEnabled() 检查指纹是否启用
 * - 使用 fingerprint 模块的 getUrlParams() 获取需要追加的参数
 * - 保留现有参数不覆盖：如果目标 URL 已包含 promotionfingerprintId，则不覆盖
 * 
 * Requirements: 2.1, 2.2, 2.3, 4.1, 4.2, 4.3, 4.4
 */
const appendPromotionIfNeeded = (url: string): string => {
  // #ifdef MP-WEIXIN
  if (!url) return url
  
  // 检查指纹系统是否启用
  if (!isEnabled()) return url
  
  // 获取需要追加的参数
  const fingerprintParams = getUrlParams()
  if (!fingerprintParams) return url

  // 解析 URL
  const [path, queryStr] = url.split('?')
  const params: Record<string, string> = {}

  // 解析现有查询参数
  if (queryStr) {
    queryStr.split('&').forEach((item) => {
      const [k, v = ''] = item.split('=')
      if (k) {
        params[k] = v
      }
    })
  }

  // 保留现有参数不覆盖：如果目标 URL 已包含 promotionfingerprintId，则不覆盖
  if (params[PROMO_ID_KEY]) return url

  // 追加指纹参数（使用 promotionfingerprintId）
  params[PROMO_ID_KEY] = encodeURIComponent(fingerprintParams[PROMO_ID_KEY])
  params[TRACE_ID_KEY] = encodeURIComponent(fingerprintParams[TRACE_ID_KEY])
  
  // 重新构建 URL
  const query = Object.entries(params)
    .map(([k, v]) => `${k}=${v}`)
    .join('&')

  return query ? `${path}?${query}` : path
  // #endif
  
  // #ifndef MP-WEIXIN
  return url
  // #endif
}

function appendCurrentShare(url: string): string {
  const currentPage = getLastPage() as PageInstance | undefined
  const share = currentPage?.$page?.fullPath?.split('#')[0].match(/[?&](share(?:=[^&]*)?)(?=&|$)/)?.[1]
  if (!share || /[?&]share(?:=|&|$)/.test(url.split('#')[0]))
    return url

  return url.replace(/^[^#]*/, (path) => {
    const separator = /[?&]$/.test(path) ? '' : path.includes('?') ? '&' : '?'
    return `${path}${separator}${share}`
  })
}

// 黑名单登录拦截器 - （适用于大部分页面不需要登录，少部分页面需要登录）
const navigateToInterceptor = {
  // 注意，这里的url是 '/' 开头的，如 '/pages/index/index'，跟 'pages.json' 里面的 path 不同
  // 增加对相对路径的处理，BY 网友 @ideal
  invoke(options: { url?: string }, carryQuery = true) {
    if (!options.url) {
      return true
    }
    
    // 同步 tabbar 状态：如果目标页面是 tabbar 页面，更新 tabbar 的选中状态
    if (carryQuery) {
      options.url = appendCurrentChannelId(options.url)
      options.url = appendCurrentShare(options.url)
    }
    const targetPath = options.url.split('?')[0]
    if (isPageTabbar(targetPath)) {
      tabbarStore.setAutoCurIdx(targetPath)
    }
    // #ifdef MP-WEIXIN
    // 先追加参数，再清除状态
    // 这样确保当前页面跳转时能带上参数，但跳转后新页面需要重新调用 initFingerprint
    if (carryQuery)
      options.url = appendPromotionIfNeeded(options.url)
    // 清除指纹状态，确保新页面必须重新调用 initFingerprint 才能继续传递
    // Requirements: 3.2, 5.1 - 页面特定状态，未调用 initFingerprint 的页面不应传递参数
    clearFingerprint()
    // #endif
    const url = options.url
    try {
      LDBridge.userInfo({}).then((res) => {
        console.log('===>', url, res)
        if (res.token) {
          uni.setStorageSync('token', res.token)
        }
      })
    } catch (error) {
      console.log('routreerror:', error)
    }

    if (!url) {
      return true
    }
    console.log(url, '===>') // /pages/route-interceptor/index?name=feige&age=30
    let path = url.split('?')[0]

    // 处理相对路径
    if (!path.startsWith('/')) {
      const currentPath = getLastPage().route
      const normalizedCurrentPath = currentPath.startsWith('/') ? currentPath : `/${currentPath}`
      const baseDir = normalizedCurrentPath.substring(0, normalizedCurrentPath.lastIndexOf('/'))
      path = `${baseDir}/${path}`
    }

    let needLoginPages: string[] = []
    // 为了防止开发时出现BUG，这里每次都获取一下。生产环境可以移到函数外，性能更好
    if (isDev) {
      needLoginPages = getNeedLoginPages()
    } else {
      needLoginPages = _needLoginPages
    }
    const isNeedLogin = needLoginPages.includes(path)
    if (!isNeedLogin) {
      return true
    }
    const hasLogin = isLogined()
    if (hasLogin) {
      return true
    }
    const redirectRoute = `${loginRoute}?redirect=${encodeURIComponent(url)}`
    uni.navigateTo({ url: redirectRoute })
    return false
  },
}

export const routeInterceptor = {
  install() {
    uni.addInterceptor('navigateTo', navigateToInterceptor)
    uni.addInterceptor('reLaunch', navigateToInterceptor)
    uni.addInterceptor('redirectTo', navigateToInterceptor)
    uni.addInterceptor('switchTab', {
      invoke(options: { url?: string }) {
        if (!options.url)
          return true
        const channelId = readChannelId(options.url) ?? getCurrentChannelId()
        const path = options.url.split('?')[0]
        // switchTab 不支持 query，待目标页面显示时再应用渠道，失败不改变当前页。
        useChannelStore().pendingTab = { path, channelId }
        options.url = path
        const allowed = navigateToInterceptor.invoke(options, false)
        if (allowed === false)
          useChannelStore().pendingTab = null
        return allowed
      },
      fail() {
        useChannelStore().pendingTab = null
      },
    })
    uni.addInterceptor('navigateBack', navigateToInterceptor)
  },
}
